/**
 * Partner Network referrer portal (inside VALUTTEN): pure derivations for the
 * Overview. No I/O, no clock, so
 * every branch is unit-tested in utils/__tests__/partnerPortal.test.ts.
 *
 * Money stays integer cents until presentation (root CLAUDE.md §6).
 */

import { REFERRAL_STAGES, type ReferralStage } from '@valutten/shared-schemas/dist/partnerNetwork';
import { timestampToMillis } from './partnerReferrals';

interface ReferralLike {
  id: string;
  stage: ReferralStage;
  referralFeeIncGstCents: number | null;
  createdAt: unknown;
  stageChangedAt?: unknown;
}

/** Count per stage, every stage present (zero when empty). */
export function pipelineCounts(referrals: ReadonlyArray<{ stage: ReferralStage }>): Record<ReferralStage, number> {
  const counts = Object.fromEntries(REFERRAL_STAGES.map((s) => [s, 0])) as Record<ReferralStage, number>;
  for (const r of referrals) if (r.stage in counts) counts[r.stage] += 1;
  return counts;
}

/** Fees on settled referrals, GST inclusive, in integer cents. Unpriced rows contribute nothing. */
export function settledFeesIncGstCents(referrals: ReadonlyArray<ReferralLike>): number {
  return referrals.reduce(
    (sum, r) =>
      r.stage === 'settled' && Number.isSafeInteger(r.referralFeeIncGstCents) ? sum + (r.referralFeeIncGstCents as number) : sum,
    0
  );
}

export interface IncomeStep {
  at: number;
  cumulativeIncGstCents: number;
}

/**
 * Cumulative settled fees over time, one step per settlement (the settled
 * stage change), oldest first. Rows without a readable time are skipped
 * rather than placed at an invented date.
 */
export function incomeSteps(referrals: ReadonlyArray<ReferralLike>): IncomeStep[] {
  const points = referrals
    .filter((r) => r.stage === 'settled' && Number.isSafeInteger(r.referralFeeIncGstCents))
    .map((r) => ({ at: timestampToMillis(r.stageChangedAt ?? r.createdAt), cents: r.referralFeeIncGstCents as number }))
    .filter((p): p is { at: number; cents: number } => p.at !== null)
    .sort((a, b) => a.at - b.at);
  let running = 0;
  return points.map((p) => {
    running += p.cents;
    return { at: p.at, cumulativeIncGstCents: running };
  });
}

/** Most recently moved first (stage change, else submission), capped. */
export function recentActivity<T extends ReferralLike>(referrals: ReadonlyArray<T>, limit = 5): T[] {
  const key = (r: T) => timestampToMillis(r.stageChangedAt ?? r.createdAt) ?? 0;
  return [...referrals].sort((a, b) => key(b) - key(a)).slice(0, limit);
}

// ─── Summary (tenantGetPartnerSummary is per partner) ───────────────────────

interface SummaryEntryLike {
  totals: {
    referred: number;
    settled: number;
    byStage: Record<ReferralStage, number>;
    feesEarnedIncGstCents: number | null;
  };
  nextPayment: { amountIncGstCents: number; count: number } | null;
  recent: { referralId: string; clientName: string; to: ReferralStage; at: string | null; feeIncGstCents: number | null }[];
  incomeSeries: { at: string | null; cumulativeIncGstCents: number }[] | null;
}

export interface AggregatedSummary {
  referred: number;
  settled: number;
  byStage: Record<ReferralStage, number>;
  /** Null when any partner's figure is hidden (hideCommissions). */
  feesEarnedIncGstCents: number | null;
  nextPayment: { amountIncGstCents: number; count: number } | null;
  recent: SummaryEntryLike['recent'];
  incomeSteps: IncomeStep[];
}

/**
 * Each partner's cumulative series back to per-entry amounts, merged by time
 * and re-accumulated. A point without a time is dropped (it cannot be placed).
 */
export function mergeIncomeSeries(series: ReadonlyArray<SummaryEntryLike['incomeSeries']>): IncomeStep[] {
  const increments: { at: number; cents: number }[] = [];
  for (const s of series) {
    if (!s) continue;
    let prev = 0;
    for (const p of s) {
      const cents = p.cumulativeIncGstCents - prev;
      prev = p.cumulativeIncGstCents;
      const at = timestampToMillis(p.at);
      if (at !== null) increments.push({ at, cents });
    }
  }
  increments.sort((a, b) => a.at - b.at);
  let running = 0;
  return increments.map((i) => ({ at: i.at, cumulativeIncGstCents: (running += i.cents) }));
}

/** Sum the per-partner summaries into the one Overview the portal shows. */
export function aggregateSummaries(entries: ReadonlyArray<SummaryEntryLike>, recentLimit = 5): AggregatedSummary {
  const byStage = Object.fromEntries(REFERRAL_STAGES.map((s) => [s, 0])) as Record<ReferralStage, number>;
  let referred = 0;
  let settled = 0;
  let earned: number | null = 0;
  let payAmount = 0;
  let payCount = 0;
  for (const e of entries) {
    referred += e.totals.referred;
    settled += e.totals.settled;
    for (const s of REFERRAL_STAGES) byStage[s] += e.totals.byStage?.[s] ?? 0;
    earned = earned === null || e.totals.feesEarnedIncGstCents === null ? null : earned + e.totals.feesEarnedIncGstCents;
    if (e.nextPayment) {
      payAmount += e.nextPayment.amountIncGstCents;
      payCount += e.nextPayment.count;
    }
  }
  const recent = entries
    .flatMap((e) => e.recent ?? [])
    .sort((a, b) => (timestampToMillis(b.at) ?? 0) - (timestampToMillis(a.at) ?? 0))
    .slice(0, recentLimit);
  return {
    referred,
    settled,
    byStage,
    feesEarnedIncGstCents: entries.length === 0 ? null : earned,
    nextPayment: payCount > 0 ? { amountIncGstCents: payAmount, count: payCount } : null,
    recent,
    incomeSteps: mergeIncomeSeries(entries.map((e) => e.incomeSeries)),
  };
}

/** Path of a referral's detail page under the portal. The id is URL-encoded. */
export function referralDetailPath(referralId: string): string {
  return `/partner-referrals/clients/${encodeURIComponent(referralId)}`;
}
